// The benches (B, in the workshop): pick a recipe, pick your materials,
// work the piece, name it, and see exactly what you would make before you
// make it.
//
// The preview is the interesting part. Nothing behind it is random
// (crafting.md decision 4; v2 decision 15), so this is not an estimate —
// it is the thing. That is why the panel shows it full size and why
// committing says "make it" rather than "try".
//
// Experimentation is the WORKING section: each aspect of the piece can be
// worked toward what the materials will bear, at a real cost in extra
// units, and pressing hard anywhere strains the rest. Everything about it
// reads as words — "worked twice", "the voice is singing", "the material
// will take no more" — never a bar, never a pip, never a count of passes.
//
// Stat chips are shown deliberately: resource stats are not a progression
// surface, and the whole promise of the bench is that a Prospector's good
// find is visible in the object.

import {
  areaLabel,
  type BenchRecipeView,
  type CraftOutcomeView,
  type FlyerLook,
  sanitizeFlyerLook,
} from "@bebop/shared";
import { escapeHtml } from "./escape";
import { statChips } from "./survey";

interface NightInfo {
  bookingId: string;
  clubId: string;
  title: string;
  artistName: string;
  hostName: string;
  scheduledFor: string | null;
  askedOfYou: boolean;
}

interface BenchResponse {
  recipes: BenchRecipeView[];
  nights: NightInfo[];
}

export type { FlyerLook };

/** Where a material is, so "enough" is never a surprise. */
function whereFrom(option: { hold: number; locker: number }): string {
  if (option.locker === 0) return `${option.hold} in hand`;
  if (option.hold === 0) return `${option.locker} in the locker`;
  return `${option.hold} in hand · ${option.locker} in the locker`;
}

/** How many times an aspect has been worked, in words. Never a digit. */
function workedWord(times: number): string {
  if (times <= 0) return "";
  if (times === 1) return "worked once";
  if (times === 2) return "worked twice";
  return "worked thrice";
}

/**
 * The made thing as an object, painted from its own palette.
 *
 * The palette is re-validated here even though the server now validates it
 * on the way out (`sanitizeFlyerLook`). Every other field on this card has
 * gone through `escapeHtml` since the day escape.ts was written; the three
 * colours went into a style ATTRIBUTE raw, which is the one place on the
 * card where a `"` ends the attribute and starts an event handler. A
 * render surface does not get to assume its input was cleaned.
 */
export function flyerCard(look: FlyerLook, title: string, night: string, footer: string): string {
  const safe = sanitizeFlyerLook(look);
  return `
    <div class="flyer" style="background:${safe.stock};color:${safe.ink};border-color:${safe.accent}">
      <div class="flyer-rule" style="background:${safe.accent}"></div>
      <div class="flyer-title">${escapeHtml(title)}</div>
      <div class="flyer-night">${escapeHtml(night)}</div>
      <div class="flyer-rule" style="background:${safe.accent}"></div>
      <div class="flyer-foot">${escapeHtml(footer)}</div>
    </div>`;
}

/** The card's second line: what a thing IS, when it is not a flyer. */
function cardLine(outcome: CraftOutcomeView): string {
  if (outcome.recipeId === "flyer") {
    return `${outcome.nightTitle || "an unnamed night"} · ${areaLabel(outcome.clubId)}`;
  }
  const aspects = outcome.aspects.map((a) => `${a.label} is ${a.word}`).join(" · ");
  return aspects || outcome.recipeId;
}

export class BenchPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;
  private bench: BenchResponse | undefined;
  /** Fired after a successful craft, so the wall outside can catch up. */
  onPrinted: (() => void) | undefined;
  private chosen: {
    recipeId: string;
    bookingId: string;
    materials: Record<string, string>;
    presses: Record<string, number>;
    title: string;
  } = { recipeId: "flyer", bookingId: "", materials: {}, presses: {}, title: "" };

  constructor(parent: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "panel-float bench-panel";
    this.root.innerHTML = `
      <div class="panel-title">THE WORKSHOP <span class="panel-hint">B to close</span></div>
      <div class="bench-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".bench-body") as HTMLDivElement;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  close(): void {
    this.open = false;
    this.root.style.display = "none";
  }

  private recipe(): BenchRecipeView | undefined {
    return this.bench?.recipes.find((r) => r.id === this.chosen.recipeId);
  }

  private async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">clearing the bench…</div>`;
    try {
      const res = await fetch("/api/bench");
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        this.body.innerHTML = `<div class="panel-empty">${escapeHtml(err.error ?? "the benches are shut")}</div>`;
        return;
      }
      this.bench = (await res.json()) as BenchResponse;
      if (!this.recipe()) {
        this.chosen.recipeId = this.bench.recipes[0]?.id ?? "flyer";
      }
      this.render();
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the benches are shut (try again)</div>`;
    }
  }

  private render(): void {
    const bench = this.bench;
    const recipe = this.recipe();
    if (!bench || !recipe) return;

    // The stations, as tabs: which bench you stand at is a choice of
    // trade, and every family lives in this one room.
    const tabs = bench.recipes
      .map(
        (r) => `
        <button class="bench-tab${r.id === this.chosen.recipeId ? " selected" : ""}" data-recipe="${escapeHtml(r.id)}">
          <span class="bench-tab-name">${escapeHtml(r.publicName)}</span>
          <span class="bench-tab-station">${escapeHtml(r.stationLabel)}</span>
        </button>`,
      )
      .join("");

    const nights = !recipe.bindsNight
      ? ""
      : bench.nights.length === 0
        ? `<div class="bench-section">THE NIGHT</div><div class="panel-empty">Nothing is billed anywhere. A flyer needs a night to advertise.</div>`
        : `<div class="bench-section">THE NIGHT</div><div class="bench-nights">${bench.nights
            .map(
              (n) => `
            <button class="bench-night${n.bookingId === this.chosen.bookingId ? " selected" : ""}" data-booking="${escapeHtml(n.bookingId)}">
              <span class="bench-night-title">${escapeHtml(n.title) || "an unnamed night"}</span>
              <span class="bench-night-line">${escapeHtml(n.artistName)} · ${escapeHtml(areaLabel(n.clubId))}</span>
              ${n.askedOfYou ? `<span class="bench-asked">${escapeHtml(n.hostName)} asked you</span>` : ""}
            </button>`,
            )
            .join("")}</div>`;

    const slots = recipe.slots
      .map((slot) => {
        const options =
          slot.options.length === 0
            ? `<div class="panel-empty">nothing you can reach will do — ${escapeHtml(slot.hint)}</div>`
            : slot.options
                .map((option) => {
                  const selected = this.chosen.materials[slot.id] === option.spawnId;
                  return `
                  <button class="bench-material${selected ? " selected" : ""}${option.enough ? "" : " short"}"
                          data-slot="${escapeHtml(slot.id)}" data-spawn="${escapeHtml(option.spawnId)}"
                          ${option.enough ? "" : "disabled"}>
                    <span class="spawn-name">${escapeHtml(option.name)}</span>
                    <span class="spawn-class">${escapeHtml(option.className)} · ${whereFrom(option)}</span>
                    <span class="spawn-stats">${statChips(option.stats)}</span>
                  </button>`;
                })
                .join("");
        return `
          <div class="bench-slot">
            <div class="bench-slot-head">${escapeHtml(slot.label)} <em>${escapeHtml(slot.hint)} · ${slot.units} units</em></div>
            ${options}
          </div>`;
      })
      .join("");

    // The working: experimentation as words. Each aspect can be worked as
    // far as the material will take; the readout is prose, never a count.
    const working =
      recipe.aspects.length === 0
        ? ""
        : `<div class="bench-section">THE WORKING</div>
           <div class="bench-note">Working an aspect spends more material and carries the piece toward what the stuff will bear. Press hard anywhere and the rest of the piece feels it.</div>
           ${recipe.aspects
             .map((aspect) => {
               const times = this.chosen.presses[aspect.id] ?? 0;
               return `
              <div class="bench-aspect">
                <span class="bench-aspect-label">${escapeHtml(aspect.label)}</span>
                <span class="bench-aspect-state">${escapeHtml(workedWord(times)) || "as it comes"}</span>
                <button class="bench-press" data-aspect="${escapeHtml(aspect.id)}" ${times >= recipe.maxPresses ? "disabled" : ""}>work it</button>
                <button class="bench-ease" data-aspect="${escapeHtml(aspect.id)}" ${times === 0 ? "disabled" : ""}>ease off</button>
              </div>`;
             })
             .join("")}`;

    const materialsChosen = recipe.slots.every((slot) => this.chosen.materials[slot.id]);
    const ready =
      materialsChosen && (!recipe.bindsNight || this.chosen.bookingId) ? "" : "disabled";
    const commitWord = recipe.id === "flyer" ? "print it" : "make it";

    this.body.innerHTML = `
      <div class="bench-tabs">${tabs}</div>
      <div class="bench-blurb">${escapeHtml(recipe.blurb)}</div>
      ${nights}
      ${slots}
      ${working}
      <div class="bench-section">THE NAME ON IT</div>
      <input class="bench-title" maxlength="60" placeholder="what it says on the piece" value="${escapeHtml(this.chosen.title)}" />
      <div class="bench-actions">
        <button class="bench-preview" ${ready}>show me</button>
        <button class="bench-craft" ${ready}>${commitWord}</button>
      </div>
      <div class="bench-result"></div>
      <div class="bench-note">The benches reach the locker as well as your hands. Units are spent when you commit, and nothing here is a gamble — what you are shown is what you get.</div>
    `;

    this.wire();
  }

  private wire(): void {
    for (const el of this.body.querySelectorAll<HTMLButtonElement>(".bench-tab")) {
      el.addEventListener("click", () => {
        const id = el.dataset.recipe ?? "flyer";
        if (id !== this.chosen.recipeId) {
          this.chosen = { recipeId: id, bookingId: "", materials: {}, presses: {}, title: "" };
        }
        this.render();
      });
    }
    for (const el of this.body.querySelectorAll<HTMLButtonElement>(".bench-night")) {
      el.addEventListener("click", () => {
        this.chosen.bookingId = el.dataset.booking ?? "";
        this.render();
      });
    }
    for (const el of this.body.querySelectorAll<HTMLButtonElement>(".bench-material")) {
      el.addEventListener("click", () => {
        const slot = el.dataset.slot ?? "";
        if (slot) this.chosen.materials[slot] = el.dataset.spawn ?? "";
        this.render();
      });
    }
    for (const el of this.body.querySelectorAll<HTMLButtonElement>(".bench-press")) {
      el.addEventListener("click", () => {
        const aspect = el.dataset.aspect ?? "";
        const max = this.recipe()?.maxPresses ?? 0;
        const now = this.chosen.presses[aspect] ?? 0;
        if (aspect && now < max) this.chosen.presses[aspect] = now + 1;
        this.render();
      });
    }
    for (const el of this.body.querySelectorAll<HTMLButtonElement>(".bench-ease")) {
      el.addEventListener("click", () => {
        const aspect = el.dataset.aspect ?? "";
        const now = this.chosen.presses[aspect] ?? 0;
        if (aspect && now > 0) this.chosen.presses[aspect] = now - 1;
        this.render();
      });
    }
    const title = this.body.querySelector<HTMLInputElement>(".bench-title");
    title?.addEventListener("input", () => {
      this.chosen.title = title.value;
    });
    // Typing a title must not walk the avatar out of the room.
    title?.addEventListener("keydown", (e) => e.stopPropagation());

    this.body
      .querySelector<HTMLButtonElement>(".bench-preview")
      ?.addEventListener("click", () => void this.send("preview"));
    this.body
      .querySelector<HTMLButtonElement>(".bench-craft")
      ?.addEventListener("click", () => void this.send("craft"));
  }

  private outcomeHtml(outcome: CraftOutcomeView, made: boolean): string {
    const materials = outcome.materials
      .map((m) => `${m.spawnName} for the ${m.slot.toLowerCase()}`)
      .join(", ");
    const aspects =
      outcome.aspects.length === 0
        ? ""
        : `<div class="bench-aspects">${outcome.aspects
            .map(
              (a) =>
                `<span class="bench-aspect-word">${escapeHtml(a.label)} is <strong>${escapeHtml(a.word)}</strong>${a.pressed ? " (worked)" : ""}</span>`,
            )
            .join("")}</div>`;
    return `
      ${flyerCard(outcome.look, outcome.title, cardLine(outcome), outcome.recipeId === "flyer" ? `${outcome.look.register} on ${outcome.look.grain} stock` : outcome.workNote)}
      ${aspects}
      <div class="bench-materials">${escapeHtml(materials)}</div>
      ${made ? `<div class="bench-done">Made. Your name is on it.</div>` : `<div class="bench-materials">${escapeHtml(outcome.workNote)}</div>`}
    `;
  }

  private async send(action: "preview" | "craft"): Promise<void> {
    const out = this.body.querySelector<HTMLDivElement>(".bench-result");
    const recipe = this.recipe();
    if (!out || !recipe) return;
    if (!this.chosen.title.trim()) {
      out.innerHTML = `<div class="panel-empty">give it a name first</div>`;
      return;
    }
    out.innerHTML = `<div class="panel-empty">${action === "craft" ? "working…" : "trying the fit…"}</div>`;
    try {
      const res = await fetch(`/api/bench/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          recipeId: this.chosen.recipeId,
          materials: this.chosen.materials,
          presses: this.chosen.presses,
          title: this.chosen.title,
          ...(recipe.bindsNight ? { bookingId: this.chosen.bookingId } : {}),
        }),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        outcome?: CraftOutcomeView;
        error?: string;
      };
      if (!res.ok) {
        out.innerHTML = `<div class="panel-empty">${escapeHtml(payload.error ?? "the bench refused")}</div>`;
        return;
      }
      const outcome = payload.outcome;
      if (!outcome) {
        out.innerHTML = `<div class="panel-empty">the bench said nothing</div>`;
        return;
      }
      out.innerHTML = this.outcomeHtml(outcome, action === "craft");
      // The hold changed; the next open should show what is left. And
      // the board on the street should be wearing a new flyer by the time
      // you walk back out.
      if (action === "craft") {
        this.onPrinted?.();
        await this.refreshAfterCraft(outcome);
      }
    } catch {
      out.innerHTML = `<div class="panel-empty">the bench refused (try again)</div>`;
    }
  }

  /** Reload stock levels but keep the finished piece on screen. */
  private async refreshAfterCraft(outcome: CraftOutcomeView): Promise<void> {
    const res = await fetch("/api/bench").catch(() => undefined);
    if (!res?.ok) return;
    this.bench = (await res.json()) as BenchResponse;
    this.chosen = {
      recipeId: this.chosen.recipeId,
      bookingId: "",
      materials: {},
      presses: {},
      title: "",
    };
    this.render();
    const out = this.body.querySelector<HTMLDivElement>(".bench-result");
    if (out) out.innerHTML = this.outcomeHtml(outcome, true);
  }
}
